import { ProductBranchSlice } from '@/store/BranchSlice';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { FaChevronDown, FaStore } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

interface BranchDropdownProps {
    filterCharts: (params: { start: number; end: number; branches: ProductBranchSlice[] }) => void;
    setFilter: (filter: string) => void;
    chartDay: number | null;
    lastMonth: number | null;
    currentMonthStart: number | null;
    currentMonthEnd: number | null;
    setSelectedBranch: React.Dispatch<React.SetStateAction<ProductBranchSlice[] | null>>;
    selectedBranch: ProductBranchSlice[] | null;
}

export default function BranchDropdown({
    filterCharts,
    currentMonthStart,
    currentMonthEnd,
    setSelectedBranch,
    selectedBranch,
}: BranchDropdownProps) {
    const selectRef = useRef<HTMLDivElement>(null);
    const [branches, setBranches] = useState<ProductBranchSlice[]>([]);
    const [branchIsOpen, setBranchIsOpen] = useState(false);
    const baseUrl = import.meta.env.VITE_BASE_URL;
    const token = useSelector((state: { auth: { token: string } }) => state.auth.token);
    const { t, i18n } = useTranslation();
    const lang = i18n.language;

    const toggleDropdown = () => {
        setBranchIsOpen(!branchIsOpen);
    };

    const handleClickOutsideBranch = (event: MouseEvent) => {
        if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
            setBranchIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutsideBranch);
        return () => {
            document.removeEventListener('mousedown', handleClickOutsideBranch);
        };
    }, []);

    const getBranches = async () => {
        try {
            const response = await axios.get(`${baseUrl}/api/dashboard/branches/list`, {
                headers: { Authorization: token },
            });
            setBranches(response.data.branches);
        } catch (error) {
            console.error(error);
        }
    };

    const handleSelectBranch = (branch: ProductBranchSlice) => {
        setSelectedBranch((prev) => {
            const current = (prev ?? []).filter((b) => b !== null && b !== undefined);
            const isSelected = current.some((b) => b._id === branch._id);

            let updated: ProductBranchSlice[] | null;
            if (isSelected) {
                updated = current.length > 1 ? current.filter((b) => b._id !== branch._id) : null;
            } else {
                updated = [branch, ...current];
            }

            if (currentMonthStart !== null && currentMonthEnd !== null) {
                filterCharts({
                    start: currentMonthStart,
                    end: currentMonthEnd,
                    branches: updated ?? [],
                });
            }

            return updated;
        });
    };

    const handleClearAll = () => {
        setSelectedBranch(null);
        if (currentMonthStart !== null && currentMonthEnd !== null) {
            filterCharts({ start: currentMonthStart, end: currentMonthEnd, branches: [] });
        }
    };

    useEffect(() => {
        getBranches();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    return (
        <div className="flex mx-auto md:mx-0 bg-white mb-10 justify-center items-center gap-5 w-full md:w-fit border rounded-md">
            <div ref={selectRef} className="relative w-full">
                <div
                    className="flex items-center gap-3 justify-between w-full px-5 py-3 min-w-[200px] cursor-pointer"
                    onClick={toggleDropdown}
                >
                    <span className="flex items-center gap-3 flex-wrap">
                        <FaStore />
                        {(selectedBranch?.length ?? 0) > 0 ? (
                            [...selectedBranch!].map((branch) => (
                                <span key={branch._id} className="px-2 py-1 text-[12px] rounded-lg bg-sidebar text-skyblue">
                                    {lang === 'en' ? branch.nameEN : branch.nameAR}
                                </span>
                            ))
                        ) : (
                            <span className="px-2 py-1 text-[12px] rounded-lg bg-sidebar text-skyblue">
                                {t("branchDropDown.AllBranches")}
                            </span>
                        )}
                    </span>
                    <FaChevronDown className="text-sm" />
                </div>
                {branchIsOpen && (
                    <ul className="absolute bg-white w-full min-w-[200px] z-[999] max-h-[300px] overflow-y-auto shadow">
                        <li
                            className={`px-3 py-2 cursor-pointer flex items-center gap-5 text-sm hover:bg-sidebar hover:text-white ${selectedBranch == null ? 'bg-sidebar text-white' : ''}`}
                            onClick={handleClearAll}
                        >
                            <label className="relative flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={selectedBranch == null}
                                    readOnly
                                    onClick={(e) => e.stopPropagation()}
                                    className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:bg-blue-600 checked:border-blue-600 shadow hover:shadow-md"
                                />
                                <span className="absolute text-white opacity-0 peer-checked:opacity-100 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[12px]">
                                    ✔
                                </span>
                            </label>
                            {t("branchDropDown.AllBranches")}
                        </li>

                        {branches.map((branch) => {
                            const isSelected = selectedBranch?.some((b) => b._id === branch._id) ?? false;
                            return (
                                <li
                                    key={branch._id}
                                    className={`px-3 py-2 flex items-center gap-5 cursor-pointer text-sm hover:bg-sidebar hover:text-white ${isSelected ? 'bg-sidebar text-white' : ''}`}
                                    onClick={() => handleSelectBranch(branch)}
                                >
                                    <label className="relative flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            readOnly
                                            onClick={(e) => e.stopPropagation()}
                                            className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:bg-blue-600 checked:border-blue-600 shadow hover:shadow-md"
                                        />
                                        <span className="absolute text-white opacity-0 peer-checked:opacity-100 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[12px]">
                                            ✔
                                        </span>
                                    </label>
                                    {lang === 'en' ? branch.nameEN : branch.nameAR}
                                </li>

                            );
                        })}
                       
                    </ul>
                )}
            </div>
        </div>
    );
}
